import { useSelectedProductId } from "hooks/useSelectedProductId";
import { observer } from "mobx-react";
// teacher env specific navigation menu
import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { NavigationMenu } from "components/NavigationMenu/NavigationMenu";
import { ProductType } from "constants/hst-constants";
import {
  PLACEHOLDER_PAGE_ROUTES,
  TEACHER_HOME_PAGE_ROUTES,
  TEACHER_INSIGHT_PAGE_ROUTES,
  TEACHER_SCHEDULED_TEST_PAGE_ROUTES,
} from "constants/routes";
import { useDomainHandler } from "hooks/useDomainHandler";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useModuleAvailability } from "hooks/useModuleAvailability";
import { useProduct } from "hooks/useProduct";
import { useLanguage } from "hooks/useSelectedLanguage";
import type { User } from "models/user/User";
import { useLocation } from "react-router-dom";

import { FontAwesomeIcon } from "components/FontAwesomeIcon";
import {
  StyledDropdownIcon,
  StyledIcon,
  StyledNavigationItemName,
  StyledOrphanNavigationItemName,
} from "components/NavigationMenu/NavigationMenu.styled";
import {
  HstProductsWithPlaceholderModules,
  getComingSoonModules,
} from "constants/placeholder-constants";
import { Box, CssFlex } from "styles/helpers/layout";
import { hogesTheme } from "themes/hoges";

interface ComponentProps {
  className?: string;
  userDetails?: User;
  shouldShowSideNav: boolean;
  dataCy?: string;
}

export const TeacherNavigationMenu: React.FC<ComponentProps> = observer(
  ({ className, userDetails, dataCy }) => {
    const { showComingSoonModules } = useFeatureFlags();
    const { t } = useTranslation("homepage");
    const { currentLanguage } = useLanguage();
    const { getStudentTermByDomain } = useDomainHandler();
    const location = useLocation();

    const { productsList, productDetails } = useProduct();
    const isTeacherAndCanInviteStudents = useMemo(
      () => userDetails?.allowedInviteProducts as boolean,
      [userDetails?.allowedInviteProducts],
    );
    const isTeacherABusinessUser = productsList?.every(
      (product) => product.type === ProductType.BUSINESS,
    );
    const productId = useSelectedProductId();

    const { isAdaptiveModuleAvailable, isWritingModuleAvailable } = useModuleAvailability();

    const isInsightsExpanded = location.pathname.includes("/insights");
    const isTestingExpanded = location.pathname.includes("/scheduled-tests"); // fix: match plural path used in routes (ensures Testing group expands and test menu items render for Cypress selectors)

    const buildURLWithProductId = (path: string) => {
      return buildUrlWithPathParams(path, { productId: productId || "" });
    };

    const menuItems = useMemo(
      () => [
        ...(isTeacherAndCanInviteStudents
          ? [
              {
                id: "teacher-student-license",
                dataCy: "teacher-student-license",
                nestingLevel: 0,
                title: (
                  <StyledOrphanNavigationItemName>
                    <StyledIcon iconName="fas fa-user-plus" iconSize="lg" />
                    {t("teacherSideMenu.studentLicense.title", {
                      studentTerm: getStudentTermByDomain(),
                    })}
                  </StyledOrphanNavigationItemName>
                ),
                redirectPath: TEACHER_HOME_PAGE_ROUTES.STUDENT_LICENSE,
              },
            ]
          : []),
        {
          id: "teacher-groups",
          dataCy: "teacher-groups",
          expandedByDefault: false,
          nestingLevel: 0,
          title: (
            <StyledOrphanNavigationItemName>
              <StyledIcon iconName="fas fa-user-friends" iconSize="lg" />
              {t("teacherSideMenu.groups.title", "Groups")}
            </StyledOrphanNavigationItemName>
          ),
          redirectPath: TEACHER_HOME_PAGE_ROUTES.GROUPS,
        },
        ...(productDetails?.hasTeacherInstructions
          ? [
              {
                id: "teacher-instructions",
                dataCy: "teacher-instructions",
                nestingLevel: 0,
                title: (
                  <StyledOrphanNavigationItemName>
                    <StyledIcon iconName="far fa-file-alt" iconSize="lg" />
                    {t("teacherSideMenu.teacherInstructions.title", "Teacher instructions")}
                  </StyledOrphanNavigationItemName>
                ),
                redirectPath: buildURLWithProductId(TEACHER_HOME_PAGE_ROUTES.TEACHER_INSTRUCTIONS),
              },
            ]
          : []),
        {
          id: "teacher-insights",
          dataCy: "teacher-insights",
          expandedByDefault: isInsightsExpanded,
          title: (
            <StyledNavigationItemName>
              <StyledIcon iconName="far fa-chart-line" iconSize="lg" />
              {t("teacherSideMenu.insights.title", {
                studentTerm: getStudentTermByDomain({ usePlural: true }),
              })}
              <StyledDropdownIcon iconName="fas fa-angle-down" iconSize="lg" />
            </StyledNavigationItemName>
          ),
          subItems: [
            ...(isAdaptiveModuleAvailable
              ? [
                  {
                    id: "teacher-progress-insights",
                    dataCy: "teacher-progress-insights",
                    title: t("teacherSideMenu.insights.progress.title", "Basic skills"),
                    redirectPath: buildURLWithProductId(
                      TEACHER_INSIGHT_PAGE_ROUTES.PROGRESS_OVERVIEW,
                    ),
                  },
                ]
              : []),
            ...(isWritingModuleAvailable
              ? [
                  {
                    id: "teacher-writing-insights",
                    dataCy: "teacher-writing-insights",
                    title: t("teacherSideMenu.insights.writing.title", "Writing skills"),
                    redirectPath: buildURLWithProductId(TEACHER_INSIGHT_PAGE_ROUTES.WRITING_SKILLS),
                  },
                ]
              : []),
            // Coming soon modules
            ...(showComingSoonModules &&
            productId &&
            HstProductsWithPlaceholderModules.includes(productId)
              ? getComingSoonModules(t).map((module) => ({
                  id: `coming-soon-module-${module.type}`,
                  title: (
                    <CssFlex
                      gap={0.5}
                      alignItems="center"
                      justifyContent="space-between"
                      paddingRight="0.8rem"
                      width="100%"
                    >
                      {module.title}
                      <Box
                        background={hogesTheme.palette.red._950}
                        borderRadius={100}
                        padding="0.5rem"
                        height="1.5rem"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <FontAwesomeIcon
                          iconName="far fa-hourglass"
                          iconSize="sm"
                          style={{
                            color: hogesTheme.palette.neutral._200,
                            fontSize: "12px",
                          }}
                        />
                      </Box>
                    </CssFlex>
                  ),
                  nestingLevel: 1,
                  dataCy: `coming-soon-${module.type}-module-item`,
                  redirectPath: buildUrlWithPathParams(PLACEHOLDER_PAGE_ROUTES.COMING_SOON_MODULE, {
                    productId,
                    moduleType: module.type,
                  }),
                }))
              : []),
          ],
        },
        ...(!isTeacherABusinessUser
          ? [
              {
                id: "teacher-testing",
                dataCy: "teacher-testing",
                expandedByDefault: isTestingExpanded,
                title: (
                  <StyledNavigationItemName>
                    <StyledIcon iconName="far fa-flag" iconSize="lg" />
                    {t("teacherSideMenu.testing.title", "Testing")}
                    <StyledDropdownIcon iconName="fas fa-angle-down" iconSize="lg" />
                  </StyledNavigationItemName>
                ),
                subItems: [
                  {
                    id: "teacher-create-test",
                    dataCy: "teacher-create-test",
                    title: t("teacherSideMenu.testing.createTest.title", "Create a test"),
                    redirectPath: TEACHER_SCHEDULED_TEST_PAGE_ROUTES.SCHEDULED_TESTS_OVERVIEW,
                  },
                  {
                    id: "teacher-live-test-monitoring",
                    dataCy: "teacher-live-test-monitoring",
                    title: t("teacherSideMenu.testing.monitoring.title", "Monitor live test"),
                    redirectPath:
                      TEACHER_SCHEDULED_TEST_PAGE_ROUTES.SCHEDULED_TEST_LIVE_MONITOR_OVERVIEW,
                  },
                  {
                    id: "teacher-test-report",
                    dataCy: "teacher-test-report",
                    title: t("teacherSideMenu.testing.report.title", "Test results"),
                    redirectPath:
                      TEACHER_SCHEDULED_TEST_PAGE_ROUTES.SCHEDULED_TEST_RESULTS_OVERVIEW,
                  },
                ],
              },
            ]
          : []),
      ],
      [
        currentLanguage,
        productDetails,
        isAdaptiveModuleAvailable,
        isWritingModuleAvailable,
        isTeacherABusinessUser,
        isTeacherAndCanInviteStudents,
        isInsightsExpanded,
        isTestingExpanded,
      ],
    );

    return (
      <NavigationMenu
        showGoToStudentEnvButton
        className={className}
        dataCy={dataCy}
        menuItems={menuItems}
      />
    );
  },
);
